/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { TAgency } from './common';
import { TServiceKey } from './api';

export type TPaygrade = {
    average_rating: number;
    base_client_pay_per_hour: number;
    client_pay_per_hour: number;
    estimated_matching: string;
    freelancers_available: number;
    skills_surcharge_per_hour: number;
    default_required_qualifications: string;
    default_safety_risks: string;
    default_available_facilities: string;
    default_expenses_payable: string;
};

export type TDriver = {
    large_van: number;
    medium_van: number;
    minimum_delivery_box: string;
    own_vehicle: number;
    vehicle_type: string;
    adr: number;
    hiab: number;
};

export type THospitality = {
    cocktail_making: string;
    food_preparation: string;
    heavy_lifting: string;
    role: string;
    supervisor: string;
    sous_chef: string;
    commis_chef: string;
    chef_de_partie: string;
    pastry_chef: string;
};

export type TIndustry = {
    role: string;
    forklift_reach: string;
    forklift_counterbalance: string;
};

export type TConstruction = {
    role: string;
    adt: string;
    aluminium_steel: string;
    blockwork_facework: string;
    brickwork: string;
    builder: string;
    carpentry: string;
    cis_rs: string;
    civils_operative: string;
    coded: string;
    commercial_industrial: string;
    cpcs_npors: string;
    cscs: string;
    domestic: string;
    dozer: string;
    drainage_deep_drainage: string;
    drawings_literate: string;
    dry_liner: string;
    edition_18: string;
    electrical_mate: string;
    estimator: string;
    excavator_180: string;
    excavator_360: string;
    external: string;
    fabrication: string;
    first_aid: string;
    fix_1: string;
    fix_2: string;
    foreman: string;
    forward_tip_dumper: string;
    ganger: string;
    gas_safe: string;
    gold_black_cscs: string;
    groundworks: string;
    hand_tools: string;
    handballing: string;
    housebashing: string;
    housebashing_line_work: string;
    internal: string;
    joiner: string;
    maintenance: string;
    mason: string;
    mewp_ipaf: string;
    mig_tig_stick: string;
    non_cscs: string;
    painting: string;
    painting_decorating: string;
    paper_hanging: string;
    part_1: string;
    part_2: string;
    patchwork: string;
    plastering: string;
    plumbers_mate: string;
    ppe: string;
    rams: string;
    refurb: string;
    renderer: string;
    repointing: string;
    ride_on_roller: string;
    roofing: string;
    shopfitting: string;
    shuttering_formwork: string;
    site_supervisor: string;
    skilled_labourer: string;
    skimming: string;
    slabs_edges_kerbs: string;
    smsts: string;
    spraying: string;
    stone_mason: string;
    street_works_operative: string;
    tape_jointing: string;
    telehandler: string;
    testing: string;
    woodwork: string;
};

type TOffice = {
    role: string;
    legal_secretary: boolean;
    solicitors: boolean;
    legal_administrator: boolean;
    purchasing_manager: boolean;
    planner: boolean;
    buyer: boolean;
    data_entry: boolean;
    assistant: boolean;
    sales_administrator: boolean;
    operations_assistant: boolean;
    head_receptionist: boolean;
    inbound_sales: boolean;
    outbound_sales: boolean;
    business_development_manager: boolean;
    field_sales_manager: boolean;
    telesales_representative: boolean;
    sales_executive: boolean;
    debit_collector: boolean;
    call_centre: boolean;
    account_manager: boolean;
    customer_service_manager: boolean;
    scheduler: boolean;
    sales_support: boolean;
    banking: boolean;
    retail_assistant: boolean;
    manager: boolean;
    recruitment_resourcer: boolean;
    recruitment_manager: boolean;
    temporaries_consultant: boolean;
    permanent_consultant: boolean;
    projects: boolean;
    it_manager: boolean;
    helpdesk_1st_line: boolean;
    helpdesk_2nd_line: boolean;
    helpdesk_3rd_line: boolean;
    web_designer: boolean;
    software_developer: boolean;
    it_director: boolean;
    accounts_assistant: boolean;
    accountant: boolean;
    purchase_ledger: boolean;
    sales_ledger: boolean;
    payroll: boolean;
    bookkeeper: boolean;
    finance_manager: boolean;
    quality_assurance: boolean;
    audits: boolean;
    marketing_assistant: boolean;
    marketing_manager: boolean;
    marketing_executive: boolean;
    digital_marketing: boolean;
    product_manager: boolean;
    hr_administration: boolean;
    hr_manager: boolean;
    hr_executive: boolean;
    hr_consultant: boolean;
    office_manager: boolean;
    managing_director: boolean;
    operation_manager: boolean;
    junior: boolean;
    mid_weight: boolean;
    regulatory_graphic_designer: boolean;
    junior_project_manager: boolean;
    project_coordinator: boolean;
}

export type TClientPayGrade = {
    id: number;
    service: TServiceKey;
    client: number;
    client_pay_per_hour: number;
    freelancer_pay_per_hour: number;
} & TConstruction & TDriver & THospitality & TIndustry & TOffice;

export type TApprover = {
    id: number;
    email: string;
    first_name: string;
    surname: string;
};

export type TClientData = {
    id: number;
    reference_number: string;
    email: string,
    first_name: string,
    last_name: string,
    company_name: string;
    mobile: string,
    logo: string,
    legal_name: string,
    billing_email: string,
    billing_address_1: string,
    billing_address_2: string,
    addresses_endpoint: string;
    billing_city: string;
    billing_postcode: string;
    default_admin_information: string;
    default_freelancer_information: string;
    default_nature_of_business: string;
    default_required_qualifications: string;
    default_safety_risks: string;
    default_available_facilities: string;
    default_expenses_payable: string;
    pay_by_banktransfer: boolean;
    average_score: number;
    category: string;
    agency_detail: TAgency;
    default_consultant: string;
    apply_for_credit_url?: string;
    can_apply_for_credit?: boolean;
    electronic_authorisation?: boolean;
    on_stop?: boolean;
    approvers?: TApprover[];
    referral_code?: string;
};

export type TUser = {
    meta: TMeta;
    userType: string;
    permissions: string[];
    email: string;
    fullName: string;
    isSuperUser: boolean;
    isAgent: boolean;
    isClient: boolean;
    isFreelancer: boolean;
    agency: TAgency;
    loggedIn: boolean;
    client?: TClientData;
};

type TMeta = {
    status_code: number;
    status: string;
};

export type TMatchingMode = 'MA' | 'MS' | 'MM';

export type TAgent = {
    id: number;
    full_name: string;
    photo: null | string;
    email: string;
    consultant_code: string;
};

export const DBS_CHECK_CHOICES = {
    NO_DBS: 'NO',
    DBS_APPLICATION_REQUIRED: 'AR',
    DBS_BASIC: 'BA',
    DBS_ENHANCED: 'EN',
};
export const DBS_CHECK_LABELS = {
    [DBS_CHECK_CHOICES.NO_DBS]: 'None',
    [DBS_CHECK_CHOICES.DBS_APPLICATION_REQUIRED]: 'DBS Application',
    [DBS_CHECK_CHOICES.DBS_BASIC]: 'DBS Basic',
    [DBS_CHECK_CHOICES.DBS_ENHANCED]: 'DBS Enhanced',
};
export type TDbsCheck = (typeof DBS_CHECK_CHOICES)[keyof typeof DBS_CHECK_CHOICES];

export type TCategoryChoice = {
    id: number;
    category: string;
}
